/**
 * Damage affinity — how a combatant's flesh answers a damage type.
 *
 * WHY THIS IS SYSTEM-GENERIC AND YET NOT SYSTEM-NEUTRAL. The three-way
 * resist/immune/vulnerable split is 5e's vocabulary, but it is also PF2e's,
 * Doomsworn's, and most d20 descendants'. What is NOT fixed here is the set of
 * damage TYPES: those are strings, and a system that types its damage
 * differently uses its own words in both its damage output and its affinity
 * lists. The only contract is that the two agree.
 *
 * WHY MATCHING IS ITS OWN FUNCTION. The obvious implementation —
 * `resistances.toLowerCase().includes(type)` — is what mobStore shipped, and it
 * is wrong twice over. It matches `"piercing"` inside a longer phrase, and it
 * matches SRD strings like "bludgeoning, piercing, and slashing from nonmagical
 * attacks" unconditionally, so a +1 longsword gets resisted by a creature the
 * rules say it hurts in full. Exact-token matching plus a qualifier flag is the
 * whole fix, and it belongs in one place because three call sites need it.
 */
function entryType(entry) {
    return (typeof entry === 'string' ? entry : entry.type).trim().toLowerCase();
}
function entryApplies(entry, damage) {
    if (entryType(entry) !== damage.type.trim().toLowerCase())
        return false;
    if (typeof entry !== 'string' && entry.nonmagicalOnly && damage.magical)
        return false;
    return true;
}
/**
 * Immunity beats resistance beats vulnerability. That precedence is a rule, not
 * a tidy-up: a creature listed both resistant and vulnerable to a type is a data
 * error, and halving is the safer reading of one.
 */
export function affinityVerdict(affinity, damage) {
    if (!affinity)
        return 'normal';
    if ((affinity.immune ?? []).some(e => entryApplies(e, damage)))
        return 'immune';
    if ((affinity.resistant ?? []).some(e => entryApplies(e, damage)))
        return 'resistant';
    if ((affinity.vulnerable ?? []).some(e => entryApplies(e, damage)))
        return 'vulnerable';
    return 'normal';
}
export function affinityMultiplier(verdict) {
    switch (verdict) {
        case 'immune': return 0;
        case 'resistant': return 0.5;
        case 'vulnerable': return 2;
        case 'normal': return 1;
    }
}
/**
 * Apply an affinity to a damage total. Rounds DOWN, which is 5e's rule for
 * halving and harmless for the other three multipliers.
 */
export function applyAffinity(affinity, amount, damage) {
    const verdict = affinityVerdict(affinity, damage);
    return { amount: Math.floor(amount * affinityMultiplier(verdict)), verdict };
}
const DAMAGE_TYPE_WORDS = [
    'acid', 'bludgeoning', 'cold', 'fire', 'force', 'lightning', 'necrotic',
    'piercing', 'poison', 'psychic', 'radiant', 'slashing', 'thunder',
];
/**
 * Parse an SRD-style affinity string into entries.
 *
 * Handles the two shapes stat blocks actually use:
 *   "fire, poison"                                             -> two plain entries
 *   "bludgeoning, piercing, and slashing from nonmagical attacks" -> three entries,
 *                                                                  all nonmagicalOnly
 *
 * A clause's qualifier applies to every type in that clause, which is how the
 * English reads. Unrecognised words are dropped rather than guessed at — a
 * qualifier we cannot model is better than an affinity that fires on the wrong
 * attacks.
 *
 * KNOWN LIMITS, both of which fail toward resisting more than the rules say and
 * both of which are strictly narrower than the substring match they replace:
 * a comma list that mixes plain and qualified types in ONE clause puts the
 * qualifier on all of them (stat blocks use a semicolon for that case), and
 * "not made with silvered weapons" collapses to nonmagicalOnly because
 * `IncomingDamage` has no silvered flag to consult.
 */
export function parseAffinityList(raw) {
    if (!raw)
        return [];
    const clauses = (Array.isArray(raw) ? raw : raw.split(';')).map(c => c.trim()).filter(Boolean);
    const entries = [];
    for (const clause of clauses) {
        const lower = clause.toLowerCase();
        const nonmagicalOnly = lower.includes('nonmagical');
        const types = DAMAGE_TYPE_WORDS.filter(word => new RegExp(`\\b${word}\\b`).test(lower));
        for (const type of types) {
            entries.push(nonmagicalOnly ? { type, nonmagicalOnly: true } : type);
        }
    }
    return entries;
}
//# sourceMappingURL=damage.js.map